import { useEffect, useRef } from 'react'
import { API_URL, API_HOST } from '../../config/api'
import { io, Socket } from 'socket.io-client'

interface ChatNotificationListenerProps {
  guestId?: string
  userId?: string
  isAuthenticated?: boolean
  onNewMessage?: (count: number) => void
  isActive: boolean // 聊天窗口是否打开
}

const ChatNotificationListener: React.FC<ChatNotificationListenerProps> = ({
  guestId,
  userId,
  isAuthenticated,
  onNewMessage,
  isActive
}) => {
  const socketRef = useRef<Socket | null>(null)
  const conversationIdRef = useRef<string | null>(null)
  const unreadCountRef = useRef(0)
  const onNewMessageRef = useRef(onNewMessage)

  // 保持回调函数的最新引用
  useEffect(() => {
    onNewMessageRef.current = onNewMessage
  }, [onNewMessage])

  useEffect(() => {
    // 如果聊天窗口已打开，不需要后台监听，清理连接
    if (isActive) {
      console.log('[ChatNotificationListener] Chat is active, cleaning up...')
      if (socketRef.current) {
        if (conversationIdRef.current) {
          socketRef.current.emit('leave_conversation', conversationIdRef.current)
        }
        socketRef.current.disconnect()
        socketRef.current = null
      }
      // 重置未读数
      unreadCountRef.current = 0
      return
    }

    console.log('[ChatNotificationListener] Chat is closed, starting listener...')
    console.log('[ChatNotificationListener] isAuthenticated:', isAuthenticated, 'userId:', userId, 'guestId:', guestId)

    // 初始化 Socket 连接
    const socket = io(API_HOST, {
      auth: isAuthenticated ? {
        token: localStorage.getItem('token')
      } : undefined,
      transports: ['websocket', 'polling']
    })

    socketRef.current = socket

    socket.on('connect', () => {
      console.log('[ChatNotificationListener] Socket connected, id:', socket.id)
      // 连接成功后获取会话
      initConversation()
    })

    socket.on('disconnect', (reason) => {
      console.log('[ChatNotificationListener] Socket disconnected:', reason)
    })

    socket.on('connect_error', (error) => {
      console.error('[ChatNotificationListener] Socket connect error:', error)
    })

    // 获取会话ID
    const initConversation = async () => {
      try {
        let response
        if (isAuthenticated && userId) {
          const token = localStorage.getItem('token')
          console.log('[ChatNotificationListener] Fetching conversation for authenticated user')
          response = await fetch(`${API_URL}/chat/conversation`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        } else if (guestId) {
          console.log('[ChatNotificationListener] Fetching conversation for guest:', guestId)
          response = await fetch(`${API_URL}/chat/guest/conversation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ guestId })
          })
        } else {
          console.log('[ChatNotificationListener] No userId or guestId, skipping conversation init')
          return
        }

        if (response) {
          const data = await response.json()
          console.log('[ChatNotificationListener] Conversation response:', data)
          if (data.success && data.data) {
            conversationIdRef.current = data.data.id
            socket.emit('join_conversation', data.data.id)
            console.log('[ChatNotificationListener] Joined conversation:', data.data.id)
          }
        }
      } catch (error) {
        console.error('[ChatNotificationListener] Failed to init conversation:', error)
      }
    }

    // 监听新消息
    socket.on('new_message', (message: any) => {
      console.log('[ChatNotificationListener] Received new_message event:', message)
      if (message.sender_type === 'agent') {
        unreadCountRef.current += 1
        console.log('[ChatNotificationListener] Agent message received, new unread count:', unreadCountRef.current)
        if (onNewMessageRef.current) {
          onNewMessageRef.current(unreadCountRef.current)
        }
        
        // 播放提示音
        try {
          const audio = new Audio('/notification.mp3')
          audio.play().catch(err => console.log('[ChatNotificationListener] Failed to play sound:', err))
        } catch (e) {
          console.log('[ChatNotificationListener] Audio error:', e)
        }
      }
    })

    return () => {
      console.log('[ChatNotificationListener] Cleanup: disconnecting socket')
      if (conversationIdRef.current && socket.connected) {
        socket.emit('leave_conversation', conversationIdRef.current)
      }
      socket.disconnect()
      socketRef.current = null
    }
  }, [isActive, guestId, userId, isAuthenticated]) // 注意：onNewMessage 不在依赖数组中

  return null // 这是一个无UI的监听组件
}

export default ChatNotificationListener
